/**
 * Display helpers for the scanner: grouping, ordering, bands and units.
 *
 * Pure functions only - nothing here touches the API or React, which is what
 * lets the table, the detail panels and the leads page share one set of rules
 * about what "hot" means and how a version gap is coloured.
 */
import type {
  Detection,
  Finding,
  FindingSeverity,
  ScanBand,
  SiteKind,
  TechCategory,
  VersionGap,
} from "./types";

/**
 * Category display order. The same order the API sorts by, repeated here so the
 * UI can group without trusting the array order it was handed.
 */
export const CATEGORY_ORDER: TechCategory[] = [
  "cms",
  "ecommerce",
  "pageBuilder",
  "framework",
  "library",
  "hosting",
  "cdn",
  "server",
  "language",
  "analytics",
  "advertising",
  "marketing",
  "chat",
  "consent",
  "payment",
  "booking",
  "fonts",
  "media",
  "security",
  "misc",
];

/** Group detections by category, keeping CATEGORY_ORDER and dropping empties. */
export function groupByCategory(list: Detection[]): { category: TechCategory; items: Detection[] }[] {
  const map = new Map<TechCategory, Detection[]>();
  for (const d of list) {
    const arr = map.get(d.category);
    if (arr) arr.push(d);
    else map.set(d.category, [d]);
  }
  return CATEGORY_ORDER.filter((c) => map.get(c)?.length).map((category) => ({
    category,
    items: (map.get(category) ?? []).sort((a, b) => b.confidence - a.confidence),
  }));
}

/** Severity order for rendering: worst first, wins last. */
export const SEVERITY_ORDER: FindingSeverity[] = ["critical", "warning", "info", "good"];

export function sortFindings(list: Finding[]): Finding[] {
  return [...list].sort((a, b) => {
    const sa = SEVERITY_ORDER.indexOf(a.severity);
    const sb = SEVERITY_ORDER.indexOf(b.severity);
    if (sa !== sb) return sa - sb;
    return b.score - a.score;
  });
}

/**
 * Score band. The bands are deliberately coarse: the number is an estimate, and
 * an operator only ever acts on "worth a call" versus "leave it".
 */
export type ScoreBand = ScanBand;

export function scoreBand(score: number): ScoreBand {
  if (score >= 65) return "hot";
  if (score >= 40) return "warm";
  if (score >= 20) return "cool";
  return "healthy";
}

/** Band colours, shared by the dots in the table and the chips in the filters. */
export const BAND_DOT: Record<ScoreBand, string> = {
  hot: "bg-rose-500",
  warm: "bg-amber-500",
  cool: "bg-sky-500",
  healthy: "bg-emerald-500",
};

export const BAND_TEXT: Record<ScoreBand, string> = {
  hot: "text-rose-600 dark:text-rose-400",
  warm: "text-amber-600 dark:text-amber-400",
  cool: "text-sky-600 dark:text-sky-400",
  healthy: "text-emerald-600 dark:text-emerald-400",
};

/** The same four bands as an SVG stroke, for the score dial's arc. */
export const BAND_RING: Record<ScoreBand, string> = {
  hot: "stroke-rose-500",
  warm: "stroke-amber-500",
  cool: "stroke-sky-500",
  healthy: "stroke-emerald-500",
};

/**
 * Site kinds, in the order the filter chips show them: the ones the owner's
 * revenue passes through first, the ones nothing depends on last.
 */
export const KIND_ORDER: SiteKind[] = [
  "shop",
  "booking",
  "portal",
  "leadgen",
  "content",
  "brochure",
  "portfolio",
  "unknown",
];

/**
 * Criticality bands. Coarse for the same reason the score bands are: the number
 * is an estimate, and the decision it feeds is "call, email, or leave it".
 *
 * `low` is the one that matters - it is the band the lead evaluator refuses to
 * call hot, so the colour on screen has to agree with what the list does.
 */
export type CriticalityBand = "core" | "active" | "light" | "low";

export function criticalityBand(criticality: number | null): CriticalityBand | null {
  if (criticality == null) return null;
  if (criticality >= 70) return "core";
  if (criticality >= 45) return "active";
  if (criticality >= 30) return "light";
  return "low";
}

/** Deliberately not the score's palette: this axis is not a verdict on quality. */
export const CRITICALITY_TEXT: Record<CriticalityBand, string> = {
  core: "text-foreground",
  active: "text-foreground",
  light: "text-muted-foreground",
  low: "text-muted-foreground",
};

export const CRITICALITY_BAR: Record<CriticalityBand, string> = {
  core: "bg-primary",
  active: "bg-primary/70",
  light: "bg-muted-foreground/50",
  low: "bg-muted-foreground/35",
};

/**
 * How a version gap is shown. `unknown` is muted on purpose: a version we could
 * not read must never look like a problem we found.
 */
export const GAP_CLASS: Record<VersionGap, string> = {
  major: "text-rose-600 dark:text-rose-400",
  minor: "text-amber-600 dark:text-amber-400",
  patch: "text-sky-600 dark:text-sky-400",
  current: "text-emerald-600 dark:text-emerald-400",
  ahead: "text-emerald-600 dark:text-emerald-400",
  unknown: "text-muted-foreground",
};

/** Bytes to a short human string ("312 kB"). */
export function formatBytes(bytes: number | null): string {
  if (bytes == null) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Milliseconds to a short human string ("1.2 s"). */
export function formatMs(ms: number | null): string {
  if (ms == null) return "-";
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

/** Cents to a short euro string ("1.2 M", "340 k"). */
export function formatEurShort(cents: number | null): string {
  if (cents == null) return "-";
  const eur = cents / 100;
  if (eur >= 1_000_000) return `${(eur / 1_000_000).toFixed(1)} M`;
  if (eur >= 1000) return `${Math.round(eur / 1000)} k`;
  return String(Math.round(eur));
}

/** Whole years since a date, for "this site has not been touched in N years". */
export function yearsSince(iso: string | null): number | null {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  return Math.floor((Date.now() - then.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
}

/**
 * A timestamp the way an operator reads it: the clock for today, the date for
 * anything older. Used in every "last checked" column.
 */
export function formatWhen(iso: string | null, locale = "et-EE"): string {
  if (!iso) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";
  const sameDay = new Date().toDateString() === date.toDateString();
  return date.toLocaleString(locale,
    sameDay ? { hour: "2-digit", minute: "2-digit" } : { dateStyle: "short", timeStyle: "short" },
  );
}

/** Relative age in whole days, for "checked 4 days ago". Null for today. */
export function daysAgo(iso: string | null): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  return Math.floor((Date.now() - then) / (24 * 60 * 60 * 1000));
}
